import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';

export interface OrgChartJunctionNodeData extends Record<string, unknown> {
  isHighlighted?: boolean;
  isDimmed?: boolean;
}

/**
 * Junction node - an invisible connector point used to create
 * clean vertical spine + horizontal branch edge routing.
 * 
 * Has handles on all four sides to support various connection patterns:
 * - Top: receives connection from parent/previous junction
 * - Bottom: sends connection to next junction in spine
 * - Left/Right: sends connection to person nodes (branches)
 */
const OrgChartJunctionNode: React.FC<NodeProps> = ({ data }) => {
  const nodeData = data as OrgChartJunctionNodeData;
  
  return (
    <div 
      className="relative"
      style={{ 
        width: 1, 
        height: 1,
        opacity: nodeData.isDimmed ? 0.2 : 1,
        pointerEvents: 'none',
      }}
    >
      {/* Top handle - receives from parent or previous junction */}
      <Handle
        type="target"
        position={Position.Top}
        className="!opacity-0 !bg-transparent !border-0 !w-0 !h-0"
        style={{ pointerEvents: 'none' }}
      />
      {/* Bottom handle - connects to next junction in spine */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!opacity-0 !bg-transparent !border-0 !w-0 !h-0"
        style={{ pointerEvents: 'none' }}
      />
      {/* Left handle - connects to person node on the left */}
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        className="!opacity-0 !bg-transparent !border-0 !w-0 !h-0"
        style={{ pointerEvents: 'none' }}
      />
      {/* Right handle - connects to person node on the right */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!opacity-0 !bg-transparent !border-0 !w-0 !h-0"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
};

export default memo(OrgChartJunctionNode);
